import { Component, ElementRef, Inject, Input, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { CheckIsCodeExistsRequest, CreateSupplierRequest, CRM_QuanHuyenDto, CRM_TinhThanhDto, SupplierClient, SupplierTypeEnum } from 'src/app/shared/api-connector/sso-connector';
import { validateEmailFormat, validateMobilephone } from 'src/app/shared/custom-validation';
import { AlertService } from 'src/app/shared/services/alert.service';
import { DrawerRef } from 'src/app/shared/components/drawer/drawer-ref';
import { AutocompleteCacheService } from 'src/app/shared/services/autocomplete-cache.service';

@Component({
  selector: 'app-add-supplier-dialog',
  templateUrl: './add-supplier-dialog.component.html',
  styleUrls: ['./add-supplier-dialog.component.scss']
})
export class AddSupplierDialogComponent implements OnInit {
  maTinhThanh: string;
  maQuanHuyen: string;
  myForm = new FormGroup({
    codeCtrl: new FormControl('', {
      updateOn: 'change',
      asyncValidators: [this.validateCodeNotTaken.bind(this)],
    }),
    nameCtrl: new FormControl('',
      {
        validators: [Validators.required, Validators.maxLength(255)],
        updateOn: 'change',
        asyncValidators: [this.validateNameNotTaken.bind(this)],
      }
    ),
    phoneCtrl: new FormControl('', {
      validators: [Validators.required, validateMobilephone, Validators.maxLength(50)],
    }),
    tinhThanhCodeCtrl: new FormControl(''),
    quanHuyenCodeCtrl: new FormControl(''),
    phuongXaCodeCtrl: new FormControl(''),
    companyCtrl: new FormControl(''),
    maSoThueCtrl: new FormControl('', [Validators.maxLength(100)]),
    emailCtrl: new FormControl('', [Validators.maxLength(100), validateEmailFormat]),
    noteCtrl: new FormControl('', [Validators.maxLength(2000)]),
    addressCtrl: new FormControl('', [Validators.maxLength(2000)]),
  });
  ctrls = this.myForm.controls;
  type: number;
  @Input() data: any;
  drawerRef: DrawerRef<AddSupplierDialogComponent>;

  constructor(
    @Optional() public dialogRef: MatDialogRef<AddSupplierDialogComponent>,
    public supplierClient: SupplierClient,
    private spinner: NgxSpinnerService,
    private alertService: AlertService,
    private el: ElementRef,
    private autocompleteCache: AutocompleteCacheService,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {
  }

  ngOnInit(): void {
    // Determine data source (Dialog or Drawer)
    const effectiveData = this.data || this.dialogData;
    this.type = effectiveData?.type ?? SupplierTypeEnum.NHA_CUNG_CAP;
    this.ctrls.tinhThanhCodeCtrl.valueChanges.subscribe(x => {
      //console.log(x)
      if (x instanceof CRM_TinhThanhDto && x) {
        this.maTinhThanh = x.maTinhThanh;
      } else {
        this.maTinhThanh = x;
      }
    });

    this.ctrls.quanHuyenCodeCtrl.valueChanges.subscribe(x => {
      //console.log(x)
      if (x instanceof CRM_QuanHuyenDto && x) {
        this.maQuanHuyen = x.maQuanHuyen;
      } else {
        this.maQuanHuyen = x;
      }
    });
  }

  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  validateCodeNotTaken(control: FormControl): any {
    if (control.value) {
      return this.supplierClient.checkIsCodeExists(new CheckIsCodeExistsRequest({
        code: this.ctrls.codeCtrl.value,
        supplierType: this.type
      })).pipe(
        map(res => {
          return res ? { codeTaken: true } : null;
        }));
    } else {
      return of(null);
    }
  }

  validateNameNotTaken(control: FormControl): any {
    if (control.value) {
      return this.supplierClient.checkIsNameExists(new CheckIsCodeExistsRequest({
        code: this.ctrls.nameCtrl.value,
        supplierType: this.type
      })).pipe(
        map(res => {
          return res ? { nameTaken: true } : null;
        }));
    } else {
      return of(null);
    }
  }

  close(result?: any) {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else if (this.drawerRef) {
      this.drawerRef.close(result);
    }
  }

  onfocusInvalid() {
    for (const key of Object.keys(this.myForm.controls)) {
      if (this.myForm.controls[key].invalid) {
        const invalidControl = this.el.nativeElement.querySelector('[formcontrolname="' + key + '"]');
        invalidControl.focus();
        break;
      }
    }
  }

  onSubmit() {
    this.myForm.markAllAsTouched();
    this.onfocusInvalid();
    if (this.myForm.valid) {
      this.spinner.show();
      this.supplierClient.createOrUpdate(new CreateSupplierRequest({
        id: 0,
        name: this.ctrls.nameCtrl.value,
        code: this.ctrls.codeCtrl.value,
        phone: this.ctrls.phoneCtrl.value,
        company: this.ctrls.companyCtrl.value,
        tinhThanhCode: this.ctrls.tinhThanhCodeCtrl.value ? this.ctrls.tinhThanhCodeCtrl.value.maTinhThanh : null,
        quanHuyenCode: this.ctrls.quanHuyenCodeCtrl.value ? this.ctrls.quanHuyenCodeCtrl.value.maQuanHuyen : null,
        phuongXaCode: this.ctrls.phuongXaCodeCtrl.value ? this.ctrls.phuongXaCodeCtrl.value.maPhuongXa : null,
        maSoThue: this.ctrls.maSoThueCtrl.value,
        email: this.ctrls.emailCtrl.value,
        note: this.ctrls.noteCtrl.value,
        address: this.ctrls.addressCtrl.value,
        branchId: 1,
        isActive: true,
        debt: 0,
        supplierType: this.type
      }))
        .pipe(finalize(() => this.spinner.hide()))
        .subscribe(x => {
          this.alertService.success('Thêm mới nhà cung cấp thành công', 'Thành công');
          this.autocompleteCache.clearType('supplier_');
          this.close(x);
        });
    }
  }
}
